import EventEmitter from "events";
import { RobotTwist } from "../../../api/robocore/dex_interfaces";
import { IAgent, ModelBasedVisual } from "../../../visuals/ModelBasedVisual";
import { AgentState } from "./AgentState";
import { NavGoalVisualizer } from "../robot/visualizers/NavGoalVisualizer";
import { SimAgentUpdate } from "../scenarios/ScenarioClient";
import { copyVec3 } from "../../../visuals/visual-utils";
import { BaseVisual } from "../../../visuals/BaseVisual";

export enum AgentEvents {
  StateChange = 'StateChange',
  AgentStateChange = 'AgentStateChange',
  ViewManagerChange = 'ViewManagerChange'
}

export class BaseAgent implements IAgent {
  public states = new Map<string, AgentState>();
  public navGoal: number[] = [];
  public navPath?: number[] | undefined;
  public euler?: number[] | undefined;
  public navGoalVisualizer: NavGoalVisualizer;
  public maxSpeed = 1.0; // meters per second
  public robotTwist: RobotTwist = { linear: [0, 0, 0], angular: [0, 0, 0] };
  private eventer = new EventEmitter();
  public isDirty = false;
  public properties: { [key: string]: any } = {};

  constructor(public visual: BaseVisual) {
    this.visual.agent = this;
    this.navGoalVisualizer = new NavGoalVisualizer(this);
    this.addState('Hidden', async () => {
      this.visual.obj.visible = false;
      return true;
    }, async () => {
      this.visual.obj.visible = true;
    });
  }

  public updateState(dgram: Partial<BaseAgent>): void {
    Object.assign(this, dgram);
    this.emit(AgentEvents.StateChange, dgram);
  }

  public getStates(): AgentState[] {
    return Array.from(this.states.values());
  }

  public getActiveState(): any {
    const activeState = this.getStates().find(s => s.active);
    return activeState ? activeState.name : '';
  }


  public on(event: AgentEvents, listener: (...args: any[]) => void): this {
    this.eventer.on(event, listener);
    return this;
  }

  public off(event: AgentEvents, listener: (...args: any[]) => void): this {
    this.eventer.off(event, listener);
    return this;
  }

  public emit(event: AgentEvents, ...args: any[]): boolean {
    return this.eventer.emit(event, ...args);
  }

  public clearListeners() {
    this.eventer.removeAllListeners();
  }

  public async setup() {
  }

  public async deactivate() {
    this.visual.wr.physXService.unregisterMessageHandler(this.visual._id);
    this.clearListeners();
  }

  public async loop() {
  }

  public render(delta: number): boolean {
    return false;
  }

  public async enableState(name: string) {
    if (!name) return;
    const state = this.states.get(name);
    if (state) {
      if (!state.active) {
        state.active = await state.start();
      }
      this.emit(AgentEvents.AgentStateChange, state);
      // } else {
      //   console.warn(`State ${name} not found`);
    }
  }

  public onSimUpdate(update: SimAgentUpdate) {
    this.visual.obj.position.copy(update.position);
    this.visual.obj.rotation.x = update.orientation.x;
    this.visual.obj.rotation.y = update.orientation.y;
    this.visual.obj.rotation.z = update.orientation.z;
    this.visual.obj.updateMatrixWorld();
    copyVec3(update.twist.linear, this.visual.velocity.linear);
    copyVec3(update.twist.angular, this.visual.velocity.angular);
  }

  public async disableAllStates(...except: string[]) {
    for (const state of this.states.values()) {
      if (state.active && !except.includes(state.name)) {
        await state.stop();
        state.active = false;
        this.emit(AgentEvents.AgentStateChange, state);
      }
    }
  }

  public async disableState(name: string) {
    const state = this.states.get(name);
    if (state) {
      await state.stop();
      state.active = false;
      this.emit(AgentEvents.AgentStateChange, state);
    } else {
      console.warn(`State ${name} not found`);
    }
  }

  public addState(name: string, start: () => Promise<boolean>, stop?: () => Promise<void>) {
    const state = new AgentState();
    state.start = start;
    state.stop = stop || (async () => { });
    this.states.set(name, state);
    state.name = name;
    return state;
  }

  public registerPhysXHandler(messageTypeId: string, handler: (data: any) => void) {
    // Implementation for registering a message handler
    const handlerPath = `${this.visual._id}/${messageTypeId}`
    this.visual.wr.physXService.registerMessageHandler(handlerPath, handler);
  }
  public getFriendlyId(): string {
    return this.visual.id || this.visual._id;
  }
}